/**
 * Watches dnd-kit's OWN `active` state from inside the DndContext and calls
 * `onActiveNull` whenever no drag is active.
 *
 * Why this exists: dnd-kit's teardown (`createHandler` in DndContext) only
 * invokes the `onDragEnd` / `onDragCancel` props when its sensorContext has
 * both `active` and `scrollAdjustedTranslate` populated at drop time. The
 * sensorContext is filled by an effect AFTER the DragStart state flush, and
 * sensors listen on `document` (native listeners, microtask-batched in React
 * 18) — so a drop landing before that flush tears the drag down internally
 * WITHOUT ever calling our handlers. dnd-kit's monitor events are skipped by
 * the same `if (event)` guard, so `useDndMonitor` can't see it either.
 *
 * When that happens, the board's own drag state (`activeItem`,
 * `draggedColumnId`, the hook's `dragState`) leaks — and a leaked
 * `activeItem` renders a second dashed "dragging" placeholder on the next
 * drag (two items appear to drag at once). Observing the context value
 * directly is the only teardown signal dnd-kit cannot skip.
 */

import { useDndContext } from '@dnd-kit/core';
import { useEffect, useRef } from 'react';

export function DndActiveSentinel({
  onActiveNull,
}: {
  onActiveNull: () => void;
}) {
  const { active } = useDndContext();
  const isActive = active != null;
  // Latest-callback ref: keeps the effect from re-firing when only
  // `onActiveNull`'s reference changes (it's not guaranteed stable by
  // callers), so it depends solely on the value that should trigger it.
  const onActiveNullRef = useRef(onActiveNull);
  onActiveNullRef.current = onActiveNull;
  useEffect(() => {
    if (!isActive) {
      onActiveNullRef.current();
    }
  }, [isActive]);
  return null;
}
